/**
 * 指挥页队员标记 / 弹窗 DOM 工厂（从 Dashboard.js 拆出）
 */
import {
  APP_PIN_COLORS,
  LOCUS_MARKER_ICONS,
  ONLINE_TIMEOUT,
  DEFAULT_OFFLINE_TIMEOUT,
  PRESENCE_STATUS
} from './constants';
import {
  resolveMemberIconKey,
  chromeStatusForMarkerState,
  toChromeIconKey,
  parseMemberIconRef,
  createTeardropMarkerElement,
  createColorPinElement,
  createAppMarkerElement
} from '../../modules/iconLibrary';
import { getMemberDisplayName } from './deviceIdentity';

export {
  createTeardropMarkerElement,
  createColorPinElement,
  createAppMarkerElement
};

export function isZhxtAppMember(member) {
  return !!(
    member &&
    (member.client_source === 'zhxt_app' ||
      String(member.device_unique_id || '').startsWith('zhxt_app:'))
  );
}

export function getMemberSourceLabel(member) {
  return isZhxtAppMember(member) ? '轻量 App' : 'Locus';
}

/**
 * 三态回退：online | temp_offline | offline（与方案3一致：按静默时长）
 * @param {number} [onlineTimeoutMs] 暂时离线阈值（橙灯起点）
 * @param {number} [offlineTimeoutMs] 正式离线（灰灯）
 */
export function getMemberPresenceStatus(
  member,
  now = Date.now(),
  onlineTimeoutMs = ONLINE_TIMEOUT,
  offlineTimeoutMs = DEFAULT_OFFLINE_TIMEOUT
) {
  if (!member?.last_seen) return PRESENCE_STATUS.OFFLINE;
  const t = new Date(member.last_seen).getTime();
  if (!Number.isFinite(t)) return PRESENCE_STATUS.OFFLINE;
  const age = now - t;
  const tempMs = Math.max(1000, Number(onlineTimeoutMs) || ONLINE_TIMEOUT);
  const offlineMs = Math.max(tempMs + 1000, Number(offlineTimeoutMs) || DEFAULT_OFFLINE_TIMEOUT);
  if (age < tempMs) return PRESENCE_STATUS.ONLINE;
  if (age < offlineMs) return PRESENCE_STATUS.TEMP_OFFLINE;
  return PRESENCE_STATUS.OFFLINE;
}

/** 是否仍视为「在线」（仅绿灯）；兼容旧调用 */
export function isMemberOnlineNow(member, now = Date.now(), timeout = ONLINE_TIMEOUT) {
  return getMemberPresenceStatus(member, now, timeout, Math.max(timeout + 1000, DEFAULT_OFFLINE_TIMEOUT))
    === PRESENCE_STATUS.ONLINE;
}

/** 当日是否上过线（按本机日历日看 last_seen） */
export function wasMemberOnlineToday(member, now = new Date()) {
  if (!member?.last_seen) return false;
  const t = new Date(member.last_seen);
  if (!Number.isFinite(t.getTime())) return false;
  const ref = now instanceof Date ? now : new Date(now);
  return t.getFullYear() === ref.getFullYear()
    && t.getMonth() === ref.getMonth()
    && t.getDate() === ref.getDate();
}

/**
 * 解析队员标记样式（选中 > 展开高亮 > 在线 > 暂时离线 > 离线）
 * 坐标默认灰色水滴套；状态用浅蓝/橙/红/深蓝/灰（在线/暂时离线/选中/高亮/离线）
 */
export function resolveMemberMarkerStyle(member, {
  activeMemberId,
  highlightedMemberIds,
  now = Date.now(),
  onlineTimeoutMs = ONLINE_TIMEOUT,
  offlineTimeoutMs = DEFAULT_OFFLINE_TIMEOUT,
  /** 抓取驱动三态；有则优先，不再按分钟窗口估算 */
  presenceOverride = null,
  /** 按移动速度自动切换水滴内图案（默认开） */
  speedAuto = true
} = {}) {
  const id = member.device_unique_id || member.name;
  const presence = presenceOverride
    || getMemberPresenceStatus(member, now, onlineTimeoutMs, offlineTimeoutMs);
  const isOnline = presence === PRESENCE_STATUS.ONLINE;
  const fromApp = isZhxtAppMember(member);
  let iconUrl = LOCUS_MARKER_ICONS.offline;
  let appPinColor = APP_PIN_COLORS.offline;
  let state = presence;
  let useColorPin = true;

  if (activeMemberId && id === activeMemberId) {
    iconUrl = LOCUS_MARKER_ICONS.active;
    appPinColor = APP_PIN_COLORS.active;
    state = 'active';
  } else if (highlightedMemberIds && highlightedMemberIds.has(id)) {
    iconUrl = LOCUS_MARKER_ICONS.highlighted;
    appPinColor = APP_PIN_COLORS.highlighted;
    state = 'highlighted';
  } else if (presence === PRESENCE_STATUS.ONLINE) {
    iconUrl = LOCUS_MARKER_ICONS.online;
    appPinColor = APP_PIN_COLORS.online;
  } else if (presence === PRESENCE_STATUS.TEMP_OFFLINE) {
    iconUrl = LOCUS_MARKER_ICONS.online;
    appPinColor = APP_PIN_COLORS.tempOffline;
  }

  const status = chromeStatusForMarkerState(state);
  appPinColor = status.fill;
  const glyph = resolveMemberIconKey(member, { speedAuto });
  const parsed = parseMemberIconRef(glyph);
  const glyphKey = parsed.key || 'water_drop';
  const statusDropGlyphs = new Set(['water_drop', 'stationary', 'hike', 'walk', 'person']);
  const useStatusDrop = statusDropGlyphs.has(glyphKey);
  const iconKey = useStatusDrop ? status.iconKey : toChromeIconKey(glyph);
  return {
    id,
    isOnline,
    presence,
    fromApp,
    iconUrl,
    appPinColor,
    state,
    useColorPin,
    iconKey,
    speedAuto,
    chromeUseFile: useStatusDrop && status.useFile
  };
}

export function createLocusMarkerElement(iconUrl, title) {
  const el = document.createElement('img');
  el.src = iconUrl;
  el.style.width = '28px';
  el.style.height = '36px';
  el.style.objectFit = 'contain';
  el.style.background = 'none';
  el.style.display = 'block';
  el.style.border = 'none';
  el.style.padding = '0';
  el.style.margin = '0';
  el.title = title || '未知';
  return el;
}

export function buildDetailedMemberPopupHtml(member) {
  // 兼容旧引用：详情展示统一走悬停弹层样式逻辑的精简版
  const fmt = (v, suffix = '', digits = null) => {
    if (v == null || v === '') return '未知';
    const n = Number(v);
    if (!Number.isFinite(n)) return '未知';
    return `${digits != null ? n.toFixed(digits) : n}${suffix}`;
  };
  const lon = member.last_longitude != null ? Number(member.last_longitude).toFixed(6) : '未知';
  const lat = member.last_latitude != null ? Number(member.last_latitude).toFixed(6) : '未知';
  const seen = member.last_seen ? new Date(member.last_seen).toLocaleString() : '未知';
  const brand = String(member.device_brand || member.brand || '').trim();
  const model = String(member.device_model || member.model || '').trim();
  const phone = [brand, model].filter(Boolean).join(' ') || '未知';
  return `
          <div style='min-width:160px; font-size:11px; color:#222; line-height:1.35;'>
            <div><b>姓名：</b>${getMemberDisplayName(member)}</div>
            <div><b>来源：</b>${getMemberSourceLabel(member)}</div>
            <div><b>经度：</b>${lon}</div>
            <div><b>纬度：</b>${lat}</div>
            <div><b>海拔：</b>${fmt(member.last_altitude, ' m')}</div>
            <div><b>精度：</b>${fmt(member.last_accuracy, ' m')}</div>
            <div><b>速度：</b>${fmt(member.last_speed, ' m/s', 1)}</div>
            <div><b>方向：</b>${fmt(member.last_bearing, '°', 1)}</div>
            <div><b>信号：</b>${member.gsm != null && member.gsm !== '' ? member.gsm : '未知'}</div>
            <div><b>手机：</b>${phone}</div>
            <div><b>电量：</b>${fmt(member.battery_level, '%')}</div>
            <div><b>上报：</b>${seen}</div>
          </div>
        `;
}

export function createAccuracyCircleElement(accuracyMeters) {
  const accuracyRadius = Math.max(8, Math.min(60, accuracyMeters * 3));
  const size = accuracyRadius * 2;
  const accuracySvg = document.createElement('div');
  // 给根节点明确宽高，勿用 absolute/transform，否则缩放时锚点会漂
  accuracySvg.style.width = `${size}px`;
  accuracySvg.style.height = `${size}px`;
  accuracySvg.style.pointerEvents = 'none';
  accuracySvg.style.lineHeight = '0';
  accuracySvg.innerHTML = `
            <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display:block">
              <circle 
                cx="${accuracyRadius}" 
                cy="${accuracyRadius}" 
                r="${accuracyRadius - 1}" 
                fill="none" 
                stroke="#87ceeb" 
                stroke-width="1.5" 
                opacity="0.8"
              />
              <circle 
                cx="${accuracyRadius}" 
                cy="${accuracyRadius}" 
                r="${accuracyRadius - 3}" 
                fill="#87ceeb" 
                opacity="0.15"
              />
            </svg>
          `;
  return accuracySvg;
}

export function createFlashlightElement(bearing, gradientId) {
  const flashlightSvg = document.createElement('div');
  flashlightSvg.style.width = '100px';
  flashlightSvg.style.height = '100px';
  flashlightSvg.style.pointerEvents = 'none';
  flashlightSvg.style.lineHeight = '0';
  // 旋转只加在内部 SVG，根节点 transform 留给 Mapbox
  const safeId = String(gradientId || 'beam').replace(/[^a-zA-Z0-9_-]/g, '_');
  flashlightSvg.innerHTML = `
            <svg width="100" height="100" viewBox="0 0 100 100" style="display:block;transform:rotate(${Number(bearing) || 0}deg);transform-origin:50% 50%;">
              <defs>
                <radialGradient id="beam-${safeId}" cx="50" cy="50" r="45">
                  <stop offset="0%" style="stop-color:#ffff00;stop-opacity:0.8" />
                  <stop offset="50%" style="stop-color:#ffff00;stop-opacity:0.5" />
                  <stop offset="80%" style="stop-color:#ffff00;stop-opacity:0.2" />
                  <stop offset="100%" style="stop-color:#ffff00;stop-opacity:0" />
                </radialGradient>
              </defs>
              <path d="M 50 50 L 50 5 A 45 45 0 0 1 95 50 Z" fill="url(#beam-${safeId})" />
            </svg>
          `;
  return flashlightSvg;
}

export function createMemberNameLabelElement(name) {
  const nameEl = document.createElement('div');
  nameEl.textContent = name || '未知';
  nameEl.style.backgroundColor = 'rgba(201, 201, 201, 0.9)';
  nameEl.style.color = 'black';
  nameEl.style.padding = '0px 3px';
  nameEl.style.borderRadius = '3px';
  nameEl.style.fontSize = '10px';
  nameEl.style.fontWeight = 'bold';
  nameEl.style.whiteSpace = 'nowrap';
  nameEl.style.writingMode = 'horizontal-tb';
  nameEl.style.textOrientation = 'mixed';
  nameEl.style.maxWidth = '60px';
  nameEl.style.overflow = 'hidden';
  nameEl.style.textOverflow = 'ellipsis';
  nameEl.style.pointerEvents = 'none';
  nameEl.style.cursor = 'default';
  return nameEl;
}
